/* eslint-disable react/prop-types */
import React, { useRef, useState } from "react";
import { FiUploadCloud, FiX } from "react-icons/fi";
import { toast } from "react-toastify";
import { ACCEPTED_FILE_TYPES, MAX_FILE_SIZE_MB } from "../constants";

/**
 * Step two: get the prescription in.
 *
 * Reading it is the slow part of the flow, so the panel says what's about to
 * happen and how long it takes rather than showing a bare spinner.
 */
const PrescriptionStep = ({ files, onFilesChange, onSubmit, isReading }) => {
    const inputRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);

    const accept = (incoming) => {
        const next = [];

        Array.from(incoming).forEach((file) => {
            if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
                toast.error(`${file.name} is over ${MAX_FILE_SIZE_MB}MB`);
                return;
            }
            next.push(file);
        });

        if (next.length) onFilesChange([...files, ...next]);
    };

    if (isReading) {
        return (
            <div className="mx-auto flex w-full max-w-xl flex-col items-center py-16 text-center">
                <span className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-cod-gray-100 border-t-elm-500 motion-safe:animate-spin" />
                <p className="mt-6 text-[15px] font-semibold text-cod-gray-900">Reading the prescription</p>
                <p className="mt-1.5 text-sm text-cod-gray-400">
                    Pulling out the medicines and matching them to the catalogue. Around a minute.
                </p>
            </div>
        );
    }

    return (
        <div className="mx-auto w-full max-w-xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-cod-gray-400">
                Add the prescription
            </p>

            <button
                type="button"
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => {
                    e.preventDefault();
                    setIsDragging(false);
                    accept(e.dataTransfer.files);
                }}
                className={`mt-3 flex w-full flex-col items-center rounded-xl border-2 border-dashed px-6 py-12 transition-colors ${isDragging ? "border-elm-400 bg-elm-50" : "border-cod-gray-200 bg-white-50 hover:border-cod-gray-300"
                    }`}
            >
                <FiUploadCloud className="text-cod-gray-300" size={26} />
                <span className="mt-4 text-[15px] font-semibold text-cod-gray-900">
                    Drop the prescription here
                </span>
                <span className="mt-1 text-xs text-cod-gray-400">
                    or click to browse · JPG, PNG or PDF up to {MAX_FILE_SIZE_MB}MB
                </span>
            </button>

            <input
                ref={inputRef}
                type="file"
                multiple
                accept={ACCEPTED_FILE_TYPES}
                className="hidden"
                onChange={(e) => {
                    accept(e.target.files);
                    e.target.value = "";
                }}
            />

            {files.length > 0 && (
                <ul className="mt-4 flex flex-col gap-2">
                    {files.map((file, index) => (
                        <li
                            key={`${file.name}-${index}`}
                            className="flex items-center justify-between gap-3 rounded-lg border border-cod-gray-100 bg-white-50 py-2.5 pl-3.5 pr-2"
                        >
                            <span className="truncate text-sm text-cod-gray-900">{file.name}</span>
                            <button
                                type="button"
                                aria-label={`Remove ${file.name}`}
                                onClick={() => onFilesChange(files.filter((_, i) => i !== index))}
                                className="shrink-0 rounded-md p-1.5 text-cod-gray-400 hover:bg-cod-gray-100 hover:text-cod-gray-900"
                            >
                                <FiX size={15} />
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            <button
                type="button"
                disabled={!files.length}
                onClick={onSubmit}
                className="mt-6 w-full rounded-xl bg-elm-600 py-3.5 text-[15px] font-semibold text-white-50 transition-colors hover:bg-elm-700 disabled:cursor-not-allowed disabled:bg-cod-gray-200"
            >
                Read prescription
            </button>
        </div>
    );
};

export default PrescriptionStep;
